import { useEffect, useRef, useState } from "react";
import { BookmarkPlus, Loader2, LogIn } from "lucide-react";
import { useAuth, startLogin } from "@/lib/auth";
import {
  listWatchlists,
  addTickerToWatchlist,
  createWatchlist,
} from "@/lib/api";
import { toast } from "@/lib/toast";
import AddToWatchlistList from "./AddToWatchlistList";

/**
 * Add-to-Watchlist control for the stock summary card.
 *
 * Logged-in:  popover with the user's watchlists + inline "new watchlist".
 * Logged-out: same affordance, click → starts Emergent Google login.
 */
export default function AddToWatchlistButton({ symbol }) {
  const { user, loading: authLoading } = useAuth();
  const [open, setOpen] = useState(false);
  const [watchlists, setWatchlists] = useState([]);
  const [fetched, setFetched] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [creatingNew, setCreatingNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setCreatingNew(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const refresh = async () => {
    setFetching(true);
    try {
      const data = await listWatchlists();
      setWatchlists(data.watchlists || []);
      setFetched(true);
    } catch {
      toast.error("Failed to load watchlists");
    } finally {
      setFetching(false);
    }
  };

  const onClickButton = () => {
    if (authLoading) return;
    if (!user) {
      startLogin();
      return;
    }
    setOpen((v) => {
      const next = !v;
      if (next && !fetched) refresh();
      return next;
    });
  };

  const addToWatchlist = async (wl) => {
    if (!symbol || addingId) return;
    if (wl.tickers?.includes(symbol)) {
      toast.info(`${symbol} already in "${wl.name}"`);
      return;
    }
    setAddingId(wl.watchlist_id);
    try {
      const updated = await addTickerToWatchlist(wl.watchlist_id, symbol);
      setWatchlists((p) =>
        p.map((w) => (w.watchlist_id === wl.watchlist_id ? updated : w))
      );
      toast.success(`${symbol} added to "${wl.name}"`);
      window.dispatchEvent(new CustomEvent("watchlists:reload"));
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setAddingId(null);
    }
  };

  const createAndAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const wl = await createWatchlist(name, [symbol]);
      setWatchlists((p) => [wl, ...p]);
      toast.success(`Created "${name}" with ${symbol}`);
      window.dispatchEvent(new CustomEvent("watchlists:reload"));
      setCreatingNew(false);
      setNewName("");
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        data-testid="add-to-watchlist-btn"
        onClick={onClickButton}
        disabled={authLoading || !symbol}
        title={user ? "Add to a watchlist" : "Sign in to save to a watchlist"}
        className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-zinc-500 text-zinc-300 hover:text-white px-3 h-9 text-[10px] font-mono uppercase tracking-widest transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {user ? <BookmarkPlus size={12} /> : <LogIn size={12} />}
        {user ? "Save" : "Sign in to save"}
      </button>

      {open && user && (
        <div
          data-testid="add-to-watchlist-popover"
          className="absolute right-0 top-full mt-1 w-72 z-50 border border-zinc-800 bg-[#0d0d0f] shadow-lg"
        >
          <div className="px-3 py-2 border-b border-zinc-900 text-label">
            Add {symbol} to…
          </div>
          {fetching ? (
            <div className="px-3 py-4 flex items-center gap-2 text-xs text-zinc-500">
              <Loader2 size={12} className="animate-spin" /> Loading…
            </div>
          ) : (
            <AddToWatchlistList
              symbol={symbol}
              watchlists={watchlists}
              addingId={addingId}
              onAdd={addToWatchlist}
              creatingNew={creatingNew}
              setCreatingNew={setCreatingNew}
              newName={newName}
              setNewName={setNewName}
              creating={creating}
              onCreate={createAndAdd}
            />
          )}
        </div>
      )}
    </div>
  );
}
